import {
    ActionIcon,
    Anchor,
    Button,
    Group,
    Modal,
    Paper,
    Stack,
    Table,
    Text,
    TextInput,
    Title,
} from '@mantine/core'
import {Dropzone} from '@mantine/dropzone'
import {useDisclosure} from '@mantine/hooks'
import {IconDownload, IconEdit, IconTrash, IconUpload} from '@tabler/icons-react'
import {useState} from 'react'

import {formatBusinessTimestamp} from 'src/api/business-time'

import type {Document} from './documents/schemas'
import {
    useArchiveCustomerDocument,
    useCreateCustomerDocument,
    useCustomerDocuments,
    useRenameCustomerDocument,
} from './documents/use-documents'
import {getCustomerDocumentDownloadUrl} from './documents/use-documents'

type CustomerDocumentsSectionProps = {
    customerId: string
}

function formatBytes(bytes: number): string {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i]
}

function formatDate(date: Date): string {
    return formatBusinessTimestamp(date, 'MMM d, yyyy')
}

export function CustomerDocumentsSection({customerId}: CustomerDocumentsSectionProps) {
    const {data: documents = [], isLoading} = useCustomerDocuments(customerId)
    const createDocument = useCreateCustomerDocument(customerId)
    const renameDocument = useRenameCustomerDocument(customerId)
    const archiveDocument = useArchiveCustomerDocument(customerId)

    const [renameOpened, {open: openRename, close: closeRename}] = useDisclosure(false)
    const [uploadOpened, {open: openUpload, close: closeUpload}] = useDisclosure(false)
    const [archiveOpened, {open: openArchive, close: closeArchive}] = useDisclosure(false)
    const [renamingDocument, setRenamingDocument] = useState<Document | null>(null)
    const [archivingDocument, setArchivingDocument] = useState<Document | null>(null)
    const [renameDraft, setRenameDraft] = useState('')
    const [uploadFile, setUploadFile] = useState<File | null>(null)
    const [uploadName, setUploadName] = useState('')
    const [errorMessage, setErrorMessage] = useState<string | null>(null)

    const saving = createDocument.isPending || renameDocument.isPending || archiveDocument.isPending

    function startRename(document: Document) {
        setRenamingDocument(document)
        setRenameDraft(document.name)
        setErrorMessage(null)
        openRename()
    }

    function startUpload() {
        setUploadFile(null)
        setUploadName('')
        setErrorMessage(null)
        openUpload()
    }

    async function handleRename() {
        if (!renamingDocument || !renameDraft.trim()) {
            setErrorMessage('Name is required')
            return
        }

        setErrorMessage(null)
        try {
            await renameDocument.mutateAsync({
                documentId: renamingDocument.id,
                input: {name: renameDraft.trim()},
            })
            closeRename()
        } catch (error) {
            setErrorMessage(error instanceof Error ? error.message : 'Failed to rename document')
        }
    }

    async function handleUpload(event: React.FormEvent) {
        event.preventDefault()
        if (!uploadFile || !uploadName.trim()) {
            setErrorMessage('File and name are required')
            return
        }

        setErrorMessage(null)
        try {
            await createDocument.mutateAsync({
                name: uploadName.trim(),
                file: uploadFile,
            })
            closeUpload()
        } catch (error) {
            setErrorMessage(error instanceof Error ? error.message : 'Failed to upload document')
        }
    }

    function startArchive(document: Document) {
        setArchivingDocument(document)
        setErrorMessage(null)
        openArchive()
    }

    async function handleArchive() {
        if (!archivingDocument) {
            return
        }

        setErrorMessage(null)
        try {
            await archiveDocument.mutateAsync(archivingDocument.id)
            closeArchive()
        } catch (error) {
            setErrorMessage(error instanceof Error ? error.message : 'Failed to archive document')
        }
    }

    return (
        <Paper withBorder p="md" radius="md">
            <Stack gap="md">
                <Group justify="space-between" align="center">
                    <Title order={3}>Documents</Title>
                    <Button
                        leftSection={<IconUpload size={16} />}
                        variant="light"
                        size="compact-sm"
                        onClick={startUpload}
                        data-testid="upload-button"
                    >
                        Upload document
                    </Button>
                </Group>

                {errorMessage ? (
                    <Text size="sm" c="red">
                        {errorMessage}
                    </Text>
                ) : null}

                {isLoading ? (
                    <Text size="sm" c="dimmed">
                        Loading...
                    </Text>
                ) : documents.length === 0 ? (
                    <Text size="sm" c="dimmed">
                        No documents yet.
                    </Text>
                ) : (
                    <Table>
                        <Table.Thead>
                            <Table.Tr>
                                <Table.Th>Name</Table.Th>
                                <Table.Th>File</Table.Th>
                                <Table.Th>Size</Table.Th>
                                <Table.Th>Created</Table.Th>
                                <Table.Th>Actions</Table.Th>
                            </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                            {documents.map((document) => (
                                <Table.Tr key={document.id}>
                                    <Table.Td>{document.name}</Table.Td>
                                    <Table.Td>{document.original_filename}</Table.Td>
                                    <Table.Td>{formatBytes(document.size_bytes)}</Table.Td>
                                    <Table.Td>{formatDate(document.created_at)}</Table.Td>
                                    <Table.Td>
                                        <Group gap="xs">
                                            <Anchor
                                                href={getCustomerDocumentDownloadUrl(
                                                    customerId,
                                                    document.id,
                                                )}
                                                download
                                                aria-label={`Download ${document.name}`}
                                            >
                                                <IconDownload size={16} />
                                            </Anchor>
                                            <ActionIcon
                                                size="sm"
                                                variant="default"
                                                aria-label={`Rename ${document.name}`}
                                                onClick={() => startRename(document)}
                                                disabled={saving}
                                            >
                                                <IconEdit size={16} />
                                            </ActionIcon>
                                            <ActionIcon
                                                size="sm"
                                                variant="default"
                                                color="red"
                                                aria-label={`Archive ${document.name}`}
                                                onClick={() => startArchive(document)}
                                                disabled={saving}
                                            >
                                                <IconTrash size={16} />
                                            </ActionIcon>
                                        </Group>
                                    </Table.Td>
                                </Table.Tr>
                            ))}
                        </Table.Tbody>
                    </Table>
                )}

                <Modal
                    opened={renameOpened}
                    onClose={closeRename}
                    title="Rename Document"
                    size="sm"
                >
                    <Stack gap="md">
                        {errorMessage ? (
                            <Text size="sm" c="red">
                                {errorMessage}
                            </Text>
                        ) : null}
                        <TextInput
                            label="Name"
                            value={renameDraft}
                            onChange={(event) => setRenameDraft(event.currentTarget.value)}
                            placeholder="Document name"
                        />
                        <Group justify="flex-end" gap="sm">
                            <Button
                                variant="default"
                                size="compact-sm"
                                onClick={closeRename}
                                disabled={saving}
                            >
                                Cancel
                            </Button>
                            <Button size="compact-sm" loading={saving} onClick={handleRename}>
                                Save
                            </Button>
                        </Group>
                    </Stack>
                </Modal>

                <Modal
                    opened={uploadOpened}
                    onClose={closeUpload}
                    title="Upload Document"
                    size="sm"
                >
                    <form onSubmit={handleUpload}>
                        <Stack gap="md">
                            {errorMessage ? (
                                <Text size="sm" c="red">
                                    {errorMessage}
                                </Text>
                            ) : null}
                            <TextInput
                                label="Name"
                                value={uploadName}
                                onChange={(event) => setUploadName(event.currentTarget.value)}
                                placeholder="Document name"
                                required
                            />
                            <Dropzone
                                onDrop={(files) => {
                                    const file = files[0] ?? null
                                    setUploadFile(file)
                                    if (file && !uploadName) {
                                        setUploadName(file.name.replace(/\.[^/.]+$/, ''))
                                    }
                                }}
                                onReject={() => {
                                    setErrorMessage('Invalid file type or size')
                                }}
                                maxSize={25 * 1024 * 1024}
                                accept={[
                                    'application/pdf',
                                    'image/png',
                                    'image/jpeg',
                                    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                                ]}
                                multiple={false}
                                data-testid="dropzone"
                            >
                                <Group
                                    justify="center"
                                    gap="xl"
                                    style={{minHeight: 120, pointerEvents: 'none'}}
                                >
                                    <Dropzone.Accept>
                                        <IconUpload size={32} />
                                    </Dropzone.Accept>
                                    <Dropzone.Reject>
                                        <IconUpload size={32} />
                                    </Dropzone.Reject>
                                    <Dropzone.Idle>
                                        <IconUpload size={32} />
                                    </Dropzone.Idle>

                                    <div>
                                        <Text size="xl" inline>
                                            Drag files here or click to select
                                        </Text>
                                        <Text size="sm" c="dimmed" inline mt={7}>
                                            PDF, PNG, JPEG, DOCX (max 25MB)
                                        </Text>
                                        {uploadFile ? (
                                            <Text size="sm" c="blue" inline mt={7}>
                                                Selected: {uploadFile.name} (
                                                {formatBytes(uploadFile.size)})
                                            </Text>
                                        ) : null}
                                    </div>
                                </Group>
                            </Dropzone>
                            <Group justify="flex-end" gap="sm">
                                <Button
                                    variant="default"
                                    size="compact-sm"
                                    onClick={closeUpload}
                                    disabled={saving}
                                    type="button"
                                >
                                    Cancel
                                </Button>
                                <Button size="compact-sm" loading={saving} type="submit">
                                    Upload
                                </Button>
                            </Group>
                        </Stack>
                    </form>
                </Modal>

                <Modal
                    opened={archiveOpened}
                    onClose={closeArchive}
                    title="Archive Document"
                    size="sm"
                >
                    <Stack gap="md">
                        {errorMessage ? (
                            <Text size="sm" c="red">
                                {errorMessage}
                            </Text>
                        ) : null}
                        <Text size="sm">
                            {archivingDocument
                                ? `Archive "${archivingDocument.name}"? This cannot be undone.`
                                : null}
                        </Text>
                        <Group justify="flex-end" gap="sm">
                            <Button
                                variant="default"
                                size="compact-sm"
                                onClick={closeArchive}
                                disabled={saving}
                            >
                                Cancel
                            </Button>
                            <Button
                                color="red"
                                size="compact-sm"
                                loading={saving}
                                onClick={handleArchive}
                            >
                                Archive
                            </Button>
                        </Group>
                    </Stack>
                </Modal>
            </Stack>
        </Paper>
    )
}
